/**
 * CenoX Home — the command centre.
 *
 * Home used to be a second chat composer whose conversation lived only in the
 * browser. Chat now has one address, `/chat`, with saved threads; Home became
 * the place you see the workspace from: what you were doing, what it costs,
 * what is connected, and the fastest way back into work.
 *
 * ── Where every number comes from ─────────────────────────────────────────
 *
 * Nothing on this page is computed or invented in the browser. Each section
 * reads one server answer, and each is asked for only when the caller may
 * have it:
 *
 *   products     GET /platform/products   — what this workspace holds
 *   threads      GET /ai/conversations    — CenoX AI held, and `ai:use`
 *   usage        GET /ai/usage            — CenoX AI held, and `settings:read`
 *
 * The server enforces all three again (tenant scope, permission, and a
 * row-level policy that keeps a private thread private), so these gates are
 * about not asking for what will be refused — never about being the guard.
 *
 * Agents and Automations have nothing behind them yet (Projects now live in
 * Chat, since #58). They get one quiet row saying they are coming, not cards
 * of zeros: a dashboard that pads itself with empty tiles reads as a
 * dashboard with nothing in it.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  ArrowRight, ArrowUpRight, BarChart3, Bot, Boxes, Gauge, MessageSquare,
  MessageSquarePlus, Send, Workflow,
} from 'lucide-react';
import type { AiConversationView, AiUsageView } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { usePlatformProducts, type PlatformProduct } from '../../lib/platformProducts';
import { lastActive, useAiConversations } from '../../lib/aiConversations';
import { count, date, money, relativeTime } from '../../lib/format';
import { CenoxMark } from '../../components/platform/CenoxMark';
import { NoWorkspaceNotice } from '../../components/platform/NoWorkspaceNotice';
import { ActivityList } from '../../components/platform/ActivityList';
import type { ChatHandover } from '../ai/ChatView';
import { cn } from '../../components/ui';

// ── Pieces ──────────────────────────────────────────────────────────────────

function Panel({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <section className={cn('rounded-2xl border border-slate-200 bg-white p-5', className)} aria-label={title}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold tracking-tight text-slate-900">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function PanelLink({ to, children }: { to: string; children: ReactNode }): ReactNode {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 transition-colors hover:text-brand-700"
    >
      {children}
      <ArrowRight className="size-3" aria-hidden />
    </Link>
  );
}

function Skeleton({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div className="space-y-2.5" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
      ))}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }): ReactNode {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tracking-tight text-slate-900 tabular">{value}</dd>
      {hint && <dd className="mt-0.5 truncate text-xs text-slate-400">{hint}</dd>}
    </div>
  );
}

function greeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 5) return 'Good evening';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Sections ────────────────────────────────────────────────────────────────

/** Ask from Home; the conversation itself happens in Chat. */
function AskBox(): ReactNode {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const prompt = draft.trim();
    if (!prompt) return;
    navigate('/chat', { state: { prompt } satisfies ChatHandover });
  };

  return (
    <form onSubmit={submit} className="mt-6">
      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white p-2 pl-4 shadow-sm transition-shadow focus-within:border-brand-400 focus-within:shadow-md">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask CenoX anything about this workspace…"
          aria-label="Ask CenoX"
          className="min-w-0 flex-1 border-0 bg-transparent py-1.5 text-sm text-slate-900 outline-none placeholder:text-slate-400"
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          aria-label="Ask"
          className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white transition-all hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Send className="size-4" aria-hidden />
        </button>
      </div>
    </form>
  );
}

function ContinueWorking({ rows, pending, failed }: { rows: AiConversationView[]; pending: boolean; failed: boolean }): ReactNode {
  const mine = rows.filter((c) => c.mine).slice(0, 4);

  return (
    <Panel title="Continue working" action={<PanelLink to="/chat">All conversations</PanelLink>}>
      {pending ? (
        <Skeleton />
      ) : failed ? (
        <p className="py-2 text-sm text-slate-500">Could not load your conversations.</p>
      ) : mine.length === 0 ? (
        <div className="flex flex-col items-start gap-3 py-2">
          <p className="text-sm text-slate-500">No conversations yet. Your first one will be here to pick up again.</p>
          <Link
            to="/chat"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            <MessageSquarePlus className="size-4" aria-hidden />
            Start a conversation
          </Link>
        </div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {mine.map((c) => (
            <li key={c.id}>
              <Link
                to={`/chat/${c.id}`}
                className="group flex h-full flex-col rounded-xl border border-slate-200 p-3.5 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
              >
                <span className="flex items-start gap-2">
                  <MessageSquare className="mt-0.5 size-4 shrink-0 text-brand-600" aria-hidden />
                  <span className="line-clamp-2 min-w-0 flex-1 text-sm font-medium text-slate-900">
                    {c.title ?? 'New conversation'}
                  </span>
                  <ArrowUpRight
                    className="size-4 shrink-0 text-slate-300 transition-colors group-hover:text-slate-500"
                    aria-hidden
                  />
                </span>
                <span className="mt-2 text-xs text-slate-500">
                  {count(c.messageCount)} message{c.messageCount === 1 ? '' : 's'} · {relativeTime(lastActive(c))}
                  {c.visibility === 'workspace' ? ' · Shared' : c.visibility === 'project' ? ' · Project' : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function UsageCard({ usage }: { usage: UseQueryResult<AiUsageView> | null }): ReactNode {
  /*
    No `settings:read`: cost is a workspace-management figure, the same rule
    the Usage page and the API apply. Said plainly, not hidden, so nobody
    wonders where it went.
  */
  if (!usage) {
    return (
      <Panel title="AI usage and costs">
        <p className="text-sm text-slate-500">
          Usage and costs are shown to people who manage this workspace&rsquo;s settings.
        </p>
      </Panel>
    );
  }

  const u = usage.data;
  const action = <PanelLink to="/ai/usage">Details</PanelLink>;

  if (usage.isPending) {
    return (
      <Panel title="AI usage and costs" action={action}>
        <Skeleton rows={2} />
      </Panel>
    );
  }
  if (usage.isError || !u) {
    return (
      <Panel title="AI usage and costs" action={action}>
        <p className="text-sm text-slate-500">Could not load usage.</p>
      </Panel>
    );
  }

  const byok = u.billingMode === 'bring_your_own_key';
  const included = Number(u.includedCredit);
  const used = Number(u.includedUsed);
  const share = included > 0 ? Math.min(1, Math.max(0, used / included)) : 0;
  const spent = included > 0 && Number(u.includedRemaining) <= 0;

  return (
    <Panel title="AI usage and costs" action={action}>
      {byok ? (
        <p className="text-sm text-slate-500">
          Your own provider key — your provider bills you directly. CenoX records what was used.
        </p>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-2xl font-semibold tracking-tight text-slate-900 tabular">
              {money(u.includedRemaining, u.currencyCode)}
            </span>
            <span className="text-xs text-slate-500">
              left of {money(u.includedCredit, u.currencyCode)}
            </span>
          </div>
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"
            role="meter"
            aria-label="Included credit used"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(share * 100)}
          >
            <div
              className={cn('h-full rounded-full transition-[width] duration-500', spent ? 'bg-red-500' : 'bg-brand-600')}
              style={{ width: `${share * 100}%` }}
            />
          </div>
        </>
      )}

      <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3">
        <div>
          <dt className="text-xs text-slate-500">Requests</dt>
          <dd className="text-sm font-medium text-slate-900 tabular">{count(u.requestsThisCycle)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Tokens</dt>
          <dd className="text-sm font-medium text-slate-900 tabular">{count(u.tokensThisCycle.total)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">Charged</dt>
          <dd className="text-sm font-medium text-slate-900 tabular">
            {money(byok ? '0' : u.chargedThisCycle, u.currencyCode)}
          </dd>
        </div>
      </dl>
      <p className="mt-3 text-xs text-slate-400">
        {u.planName} · renews {date(u.cycleEnd, 'UTC')}
      </p>
    </Panel>
  );
}

interface QuickAction {
  to: string;
  title: string;
  hint: string;
  icon: typeof Send;
}

function QuickActions({ actions }: { actions: QuickAction[] }): ReactNode {
  if (actions.length === 0) return null;
  return (
    <Panel title="Quick actions">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {actions.map((a) => (
          <Link
            key={a.title}
            to={a.to}
            className="group flex flex-col gap-3 rounded-xl border border-slate-200 p-3.5 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
          >
            <span className="flex size-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors group-hover:bg-brand-50 group-hover:text-brand-600">
              <a.icon className="size-4" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-slate-900">{a.title}</span>
              <span className="block truncate text-xs text-slate-500">{a.hint}</span>
            </span>
          </Link>
        ))}
      </div>
    </Panel>
  );
}

function ConnectedProducts({ rows, failed }: { rows: PlatformProduct[]; failed: boolean }): ReactNode {
  // CenoX AI is this workspace; the list is everything it connects to.
  const others = rows.filter((p) => p.product !== 'ai');
  const connected = others.filter((p) => p.availableAction === 'open');
  const more = others.filter((p) => p.availableAction !== 'open');

  return (
    <Panel title="Connected CenoX products">
      {failed ? (
        <p className="text-sm text-slate-500">Could not load your products.</p>
      ) : connected.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing else is set up in this workspace yet.</p>
      ) : (
        <ul className="space-y-1">
          {connected.map((p) => (
            <li key={p.product}>
              <Link
                to={`/${p.routeSlug}`}
                className="group -mx-2 flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-slate-50"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                  <Boxes className="size-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">{p.name}</span>
                  <span className="block truncate text-xs text-slate-500">{p.description}</span>
                </span>
                <span className="flex items-center gap-1.5 text-xs text-emerald-600">
                  <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden />
                  Open
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {more.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="mb-2 text-xs font-medium text-slate-500">More from CenoX</p>
          <div className="flex flex-wrap gap-1.5">
            {more.map((p) => (
              <Link
                key={p.product}
                to={`/${p.routeSlug}`}
                className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900"
              >
                {p.name.replace(/^CenoX /, '')}
                <span className="ml-1 text-slate-400">
                  {p.catalogueStatus !== 'available' ? '· Soon' : ''}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

/** Agents and Automations: named as coming, with no figures to invent. */
function ComingNext({ automation }: { automation?: PlatformProduct }): ReactNode {
  const items = [
    { to: '/ai/agents', label: 'AI Agents', icon: Bot },
    { to: automation ? `/${automation.routeSlug}` : '/automation', label: 'Automations', icon: Workflow },
  ];
  return (
    <section
      aria-label="Coming to CenoX"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-dashed border-slate-200 px-5 py-3.5"
    >
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Coming next</span>
      {items.map((i) => (
        <Link
          key={i.label}
          to={i.to}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 transition-colors hover:text-slate-900"
        >
          <i.icon className="size-4 text-slate-400" aria-hidden />
          {i.label}
        </Link>
      ))}
      <span className="text-xs text-slate-400 sm:ml-auto">Designed, not built yet — nothing to count.</span>
    </section>
  );
}

// ── The page ────────────────────────────────────────────────────────────────

export function HomeDashboard(): ReactNode {
  const { user, business, can } = useAuth();
  const path = useBusinessPath();
  const products = usePlatformProducts();

  const rows = products.data?.data ?? [];
  const ai = rows.find((p) => p.product === 'ai');
  const productsKnown = !products.isPending && !products.isError;
  const aiReady = ai?.availableAction === 'open';

  const mayChat = aiReady && can('ai:use');
  const maySeeCost = aiReady && can('settings:read');

  const threads = useAiConversations(mayChat);
  const usage = useQuery({
    queryKey: ['ai-usage', business?.businessId],
    queryFn: () => api.get<AiUsageView>(path('/ai/usage')),
    enabled: Boolean(business) && maySeeCost,
  });

  const threadRows = threads.data?.data ?? [];
  const connectedCount = rows.filter((p) => p.availableAction === 'open').length;
  const automation = rows.find((p) => p.product === 'automation');
  const businessProduct = rows.find((p) => p.product === 'business' && p.availableAction === 'open');

  const firstName = user?.fullName?.trim().split(/\s+/)[0];

  const actions: QuickAction[] = [
    ...(mayChat
      ? [
          { to: '/chat', title: 'New chat', hint: 'Start a conversation', icon: MessageSquarePlus },
          {
            to: '/chat',
            title: 'Analyse data',
            hint: 'Ask about this workspace',
            icon: BarChart3,
          },
        ]
      : []),
    ...(businessProduct
      ? [{ to: `/${businessProduct.routeSlug}`, title: 'Open Business', hint: 'Sales, stock and money', icon: Boxes }]
      : []),
    ...(maySeeCost ? [{ to: '/ai/usage', title: 'Review usage', hint: 'Tokens, requests, cost', icon: Gauge }] : []),
    {
      to: automation ? `/${automation.routeSlug}` : '/automation',
      title: 'Build a workflow',
      hint: automation?.availableAction === 'open' ? 'Open CenoX Automation' : 'In CenoX Automation',
      icon: Workflow,
    },
  ].slice(0, 4);

  const stats: Array<{ label: string; value: string; hint?: string }> = [];
  if (productsKnown) {
    stats.push({ label: 'Products in use', value: count(connectedCount), hint: `of ${count(rows.length)} in CenoX` });
  }
  if (mayChat && threads.data) {
    const n = threadRows.length;
    stats.push({
      label: 'Conversations',
      // The server lists the latest 50; past that, say so rather than undercount.
      value: n >= 50 ? '50+' : count(n),
      hint: threadRows[0] ? `last ${relativeTime(lastActive(threadRows[0]))}` : 'none yet',
    });
  }
  if (maySeeCost && usage.data) {
    stats.push({
      label: usage.data.monthlyAllowance ? 'Requests this month' : 'Requests this period',
      value: count(usage.data.requestsThisCycle),
    });
    if (usage.data.billingMode === 'cenox_managed') {
      stats.push({
        label: 'Credit left',
        value: money(usage.data.includedRemaining, usage.data.currencyCode),
        hint: `of ${money(usage.data.includedCredit, usage.data.currencyCode)}`,
      });
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      {/* ── Welcome and workspace overview ───────────────────────────────── */}
      <section
        aria-label="Welcome"
        className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white px-5 py-7 sm:px-8 sm:py-9"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-gradient-to-br from-blue-500/15 via-indigo-500/10 to-fuchsia-500/10 blur-3xl"
        />
        <div className="relative">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
            <CenoxMark className="size-4" />
            <span className="truncate">
              {business ? business.businessName : 'Personal account'}
              {business && <span className="capitalize"> · {business.role}</span>}
            </span>
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            {greeting()}
            {firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="mt-1.5 max-w-xl text-sm text-slate-500">
            Build, research, analyse, create and automate with CenoX.
          </p>

          {aiReady && mayChat && <AskBox />}

          {/*
            Said plainly, in the place the thing would have happened: which
            product it belongs to and how to get it.
          */}
          {productsKnown && !aiReady && !business && (
            <NoWorkspaceNotice className="mt-6 rounded-xl border border-slate-200 bg-white px-4 py-3" />
          )}
          {productsKnown && !aiReady && business && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-sm text-slate-600">CenoX AI is not part of this workspace yet.</p>
              <Link
                to="/ai"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {ai?.catalogueStatus === 'available' ? 'Get access' : 'Learn more'}
              </Link>
            </div>
          )}
        </div>
      </section>

      {stats.length > 0 && (
        <dl aria-label="Workspace overview" className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {stats.map((s) => (
            <Stat key={s.label} {...s} />
          ))}
        </dl>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          {mayChat && (
            <ContinueWorking rows={threadRows} pending={threads.isPending} failed={threads.isError} />
          )}
          <QuickActions actions={actions} />
          {mayChat && (
            <Panel title="Recent activity">
              {threads.isPending ? <Skeleton /> : <ActivityList rows={threadRows} limit={6} />}
            </Panel>
          )}
        </div>

        <div className="min-w-0 space-y-6">
          {aiReady && <UsageCard usage={maySeeCost ? usage : null} />}
          <ConnectedProducts rows={rows} failed={products.isError} />
        </div>
      </div>

      <ComingNext automation={automation} />
    </div>
  );
}
